/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { MoreCollateralPaymentMethod, spendingFlowScreens, WalletsTypes } from './constants';
import ModalBodyLayout from '../shared/ModalBodyLayout';
import { useSetting } from '../../utilities/sharedHooks/settings';
import { USER_PREFERENCES } from '../../utilities/sharedQueries';
import CurrencyIcon from '../../components/CurrencyIcon';
import { currencies, exchange } from '../../utilities/constants';
import {
  formatDecimalNumber,
  formatNumber,
  formatNumberWithComma,
} from '../../utilities/formatHelpers';
import { useRate } from '../../utilities/sharedHooks/rates';
import FocusableInputArea from '../Accounts/partials/FocusableInputArea';
import { validateNumbers } from '../../utilities/formValidation';
import { AMOUNT_INPUT_MAX_LENGTH } from '../Accounts/constants';
import Badge from '../../components/Badge';
import RadioItem from '../../components/RadioItem';
import { useCardStatus, useUserCardProvider } from '../../utilities/sharedHooks/user';

const AddMoreCollateral = ({
  wallet,
  openScreen,
  updatePgwRequest,
  mainScreen,
  defaultMinDeposit,
}) => {
  const { t: translate } = useTranslation();

  const [paymentMethod, setPaymentMethod] = useState(MoreCollateralPaymentMethod.PGW);

  const PREDEFINED_AMOUNTS_PERCENTAGE = [1, 1.5, 2, 3];

  const { data: cardStatus } = useCardStatus();

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const { cardProvider } = useUserCardProvider();

  const fiatCurrency = useMemo(
    () => cardStatus?.displayCurrency || userPref?.displayCurrency,
    [cardProvider, cardStatus, userPref?.displayCurrency]
  );

  const { coin, available_balance: availableBalance } = wallet;
  const rate = useRate(coin, fiatCurrency);
  const cryptoRate = useRate(fiatCurrency, coin);

  const fiatBalance = formatNumber(availableBalance * rate, exchange.FIAT_VALUE);

  const [minDeposit, setMinDeposit] = useState(0);
  const [maxDeposit, setMaxDeposit] = useState(100);

  const fiatCollateralInputSpan = useRef();
  const collateralInputSpan = useRef();

  const [fiatCollateralInputWidth, setFiatCollateralInputWidth] = useState(0);
  const [collateralInputWidth, setCollateralInputWidth] = useState(0);

  const [collateralAmount, setCollateralAmount] = useState(0);
  const [collateralAmountDecimal, setCollateralAmountDecimal] = useState(0);
  const [fiatCollateralAmount, setFiatCollateralAmount] = useState(
    formatNumber(minDeposit * rate, exchange.FIAT_VALUE)
  );

  const [feauterPgwAllowed] = useSetting('app_features', 'feature_pgw_allowed');
  const [pgwAllowCryptoExchange] = useSetting(
    'payment_gateway_options',
    'pgw_allow_crypto_exchange'
  );

  const handleChangeCollateralAmount = (event) => {
    const { value: amount, min } = event.target;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(decimalAmount, wallet?.wallet_type)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setFiatCollateralAmount(0);
      setCollateralAmountDecimal(0);
      setCollateralAmount(0);
    }

    const calcFiat = formatNumber(decimalAmount * rate, exchange.FIAT_VALUE);
    const formattedFiatCalc = formatDecimalNumber(calcFiat);

    setFiatCollateralAmount(formattedFiatCalc.formattedAmount);
    setCollateralAmount(formattedAmount);
    setCollateralAmountDecimal(decimalAmount);
  };

  const handleChangeFiatAmount = (event) => {
    const { value: amount, min } = event?.target || event;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(decimalAmount, WalletsTypes.FIAT)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setFiatCollateralAmount(0);
      setCollateralAmount(0);
    }

    const calcCrypto = formatNumber(decimalAmount * cryptoRate, exchange.CRYPTO_VALUE);
    const formattedCalcCrypto = formatDecimalNumber(calcCrypto);

    setCollateralAmount(formattedCalcCrypto.formattedAmount);
    setCollateralAmountDecimal(formattedCalcCrypto.decimalAmount);
    setFiatCollateralAmount(formattedAmount);
  };

  const paymentMethods = useMemo(
    () => [
      // {
      //   key: MoreCollateralPaymentMethod.LEDGER,
      //   icon: 'buy-sell-menu',
      //   title: translate('ledger_wallet_title'),
      //   subtitle: translate('ledger_wallet_subtitle'),
      //   onChange: () => setPaymentMethod(MoreCollateralPaymentMethod.LEDGER),
      //   disabled: true,
      // },
      {
        key: MoreCollateralPaymentMethod.PGW,
        icon: 'outline-menu',
        title: translate('card_order_payment_card_input_title'),
        subtitle: translate('card_order_payment_card_input_subtitle'),
        onChange: () => setPaymentMethod(MoreCollateralPaymentMethod.PGW),
        disabled: !feauterPgwAllowed && !pgwAllowCryptoExchange,
      },
    ],
    [feauterPgwAllowed, pgwAllowCryptoExchange]
  );

  const predefinedAmounts = useMemo(() => {
    let result = [50, 100, 200, 500];
    if (minDeposit > 0) {
      result = PREDEFINED_AMOUNTS_PERCENTAGE.map((item) => {
        const cryptoNewValue = item * minDeposit;
        const fiatNewValew = formatNumber(cryptoNewValue * rate, exchange.FIAT_VALUE);
        return fiatNewValew;
      });
      return result;
    }
    return result;
  }, [minDeposit, rate]);

  const [{ ...rest }] = useSetting('crypto_draft_options', [
    `cryptodraft_minimum_initial_deposit_amount_${wallet?.coin?.toUpperCase()}`,
    `cryptodraft_maximum_initial_deposit_amount_${wallet?.coin?.toUpperCase()}`,
  ]);

  useEffect(() => {
    const charactersLength =
      fiatCollateralInputSpan.current.value.length > 1
        ? fiatCollateralInputSpan.current.value.length
        : 1;
    // to avoid having empty input, fix length to 1 if text length is less then 1
    setFiatCollateralInputWidth(charactersLength);
  }, [fiatCollateralAmount]);

  useEffect(() => {
    const closestContainer = collateralInputSpan.current.closest('.d-flex');
    const closestContainerParent = closestContainer.closest('.modal-body');
    const charactersLength =
      collateralInputSpan.current.value.length > 1 ? collateralInputSpan.current.value.length : 2;
    // to avoid having empty input, fix length to 2 if text length is less then 1
    if (closestContainerParent.offsetWidth < closestContainer.scrollWidth) {
      closestContainer.style.fontSize = '1.5rem';
    } else if (collateralInputSpan.current.value.length < AMOUNT_INPUT_MAX_LENGTH) {
      // resize input font size to 2.125rem when char length is less then AMOUNT_INPUT_MAX_LENGTH
      closestContainer.style.fontSize = '2.125rem';
    }
    setCollateralInputWidth(charactersLength);
  }, [collateralAmount]);

  useEffect(() => {
    setCollateralAmount(minDeposit);
    setCollateralAmountDecimal(minDeposit);
    setFiatCollateralAmount(formatNumber(minDeposit * rate, exchange.FIAT_VALUE));
  }, [minDeposit, rate]);

  useEffect(() => {
    if (rest && wallet && defaultMinDeposit === null) {
      const minDepositRanges =
        rest[`cryptodraft_minimum_initial_deposit_amount_${wallet?.coin?.toUpperCase()}`] ||
        minDeposit;

      const maxDepositRanges =
        rest[`cryptodraft_maximum_initial_deposit_amount_${wallet?.coin?.toUpperCase()}`] ||
        maxDeposit;

      setMinDeposit(minDepositRanges);
      setMaxDeposit(maxDepositRanges);
    } else if (defaultMinDeposit) {
      setMinDeposit(defaultMinDeposit);
    }
  }, [wallet, rest, maxDeposit, minDeposit, defaultMinDeposit]);

  const handleFocus = (e) => {
    if (!e.target) return;
    e.target.select();
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.add('active');
    }
  };

  const handleBlurAmount = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.remove('active');
    }
  };

  const onConfirmPayment = () => {
    if (paymentMethod === MoreCollateralPaymentMethod.PGW) {
      updatePgwRequest({
        params: {
          currency: coin,
          amount: collateralAmountDecimal,
          displayCurrency: fiatCurrency,
        },
      });
      openScreen(spendingFlowScreens.PROCEED_WITH_PAYMENT);
    } else if (paymentMethod !== MoreCollateralPaymentMethod.PGW) {
      // TODO: using LEDGER hardware wallet
    }
  };

  const renderFooter = () => (
    <button
      type="button"
      className="btn modal__footer-btn py-3 btn-primary"
      disabled={collateralAmountDecimal <= 0}
      onClick={onConfirmPayment}>
      {translate('confirm_continue_btn_title')}
    </button>
  );

  const renderHeader = () => (
    <div className="d-flex flex-row align-items-center px-2 card-calculator__select-currency">
      <i
        role="button"
        tabIndex={0}
        onKeyDown={null}
        onClick={() => openScreen(mainScreen)}
        className="icon icon-arrow-back-navigation text-f4 me-2"
        aria-label="Back"
      />
      <CurrencyIcon
        currencyCode={wallet?.coin}
        style={{
          width: '1.5rem',
          height: '1.5rem',
          marginRight: '0.5rem',
        }}
      />
      <p className="m-0 me-2 text-f5">
        {translate('top_up_wallet_label').replace('__COIN__', wallet?.coin?.toUpperCase())}
      </p>
    </div>
  );

  return (
    <ModalBodyLayout renderHeader={renderHeader} renderFooter={renderFooter}>
      <div className="select-group bg-light rounded-extra p-3 d-flex flex-row mt-4 mb-4">
        <div className="d-flex flex-row align-items-center px-2 card-calculator__select-currency">
          <CurrencyIcon
            currencyCode={wallet?.coin}
            style={{
              width: '1.5rem',
              height: '1.5rem',
              marginRight: '0.5rem',
            }}
          />
          <p className="m-0 me-2 text-f5">{wallet?.coin?.toUpperCase()}</p>
        </div>
        <span className="text-right flex-fill">
          <b className="text-f5 m-0">
            {currencies[coin?.toUpperCase()].decimal === exchange.FIAT_VALUE
              ? formatNumberWithComma(
                  formatNumber(
                    availableBalance * currencies[coin?.toUpperCase()].conversion,
                    currencies[coin?.toUpperCase()].decimal
                  )
                )
              : formatNumber(
                  availableBalance * currencies[coin?.toUpperCase()].conversion,
                  currencies[coin?.toUpperCase()].decimal
                )}{' '}
            {coin?.toUpperCase()}
          </b>
          <p className="text-shadow text-f6 m-0">
            {formatNumberWithComma(
              formatNumber(
                fiatBalance * currencies[fiatCurrency?.toUpperCase()].conversion,
                exchange.FIAT_VALUE
              )
            )}{' '}
            {fiatCurrency?.toUpperCase()}
          </p>
        </span>
      </div>
      <FocusableInputArea className="mt-5 mb-1">
        <input
          required
          max={maxDeposit}
          min={0}
          type="text"
          className="form-control bg-transparent p-0 text-shadow text-center border-0"
          value={collateralAmount}
          style={{
            fontSize: '1rem',
            width: `${collateralInputWidth + 1}ch`,
            height: '3rem',
          }}
          ref={collateralInputSpan}
          onChange={(e) => handleChangeCollateralAmount(e)}
          placeholder="0 "
        />
        <span
          style={{ fontSize: 'inherit', pointerEvents: 'none' }}
          className="text-f5 text-shadow">
          {coin?.toUpperCase()}
        </span>
      </FocusableInputArea>

      <FocusableInputArea className="h1 amount-input">
        <input
          required
          min="0"
          type="text"
          className="form-control bg-transparent text-bold p-0 text-center border-0"
          value={fiatCollateralAmount}
          style={{
            fontSize: 'inherit',
            width: `${fiatCollateralInputWidth + 1}ch`,
            color: 'inherit',
            height: 'inherit',
            textIndent: fiatCollateralAmount ? 0 : '34%',
          }}
          ref={fiatCollateralInputSpan}
          onChange={(e) => handleChangeFiatAmount(e)}
          onFocus={handleFocus}
          onBlur={handleBlurAmount}
          placeholder="0  "
        />
        <b
          style={{
            fontSize: 'inherit',
            pointerEvents: 'none',
            color: 'inherit',
          }}>
          {fiatCurrency?.toUpperCase()}
        </b>
      </FocusableInputArea>

      <div className="d-flex justify-content-between mt-3">
        {predefinedAmounts.map((amount) => (
          <Badge
            key={amount}
            content={`${amount} ${fiatCurrency?.toUpperCase()}`}
            onClick={() => handleChangeFiatAmount({ value: amount, min: 0 })}
            className="px-2 py-1 text-dark text-f5 cursor-pointer rounded-max"
            style={{ width: '5rem' }}
          />
        ))}
      </div>
      <div className="mt-4">
        <p className="text-f5 text-dark">{translate('card_order_select_payment_title')}</p>
      </div>
      {paymentMethods.map((item) => (
        <RadioItem
          key={item.key}
          label={item.title}
          name="paymentMethod"
          onChange={item.onChange}
          checked={paymentMethod === item.key}
          icon={item.icon}
          subtitle={item.subtitle}
          disabled={item.disabled}
        />
      ))}
    </ModalBodyLayout>
  );
};

export default AddMoreCollateral;

AddMoreCollateral.propTypes = {
  openScreen: PropTypes.func.isRequired,
  wallet: PropTypes.object.isRequired,
  updatePgwRequest: PropTypes.func.isRequired,
  mainScreen: PropTypes.string.isRequired,
  defaultMinDeposit: PropTypes.number,
};

AddMoreCollateral.defaultProps = {
  defaultMinDeposit: null,
};
